import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import {
  LayoutDashboard,
  Package,
  Users,
  ShoppingCart,
  TrendingUp,
  Plus,
  Edit,
  Eye,
  EyeOff,
  Search,
  Filter,
  ArrowUpRight,
  ArrowDownRight,
  Leaf,
  IndianRupee,
  Box,
  UserCheck,
  Home,
  LogOut,
  FolderTree,
  Warehouse,
  Truck,
  FileText,
  Menu,
  X,
  Star,
  User,
  UserCog,
  CalendarOff,
  Clock,
  Trash2,
  AlertTriangle,
  Zap,
  ImageUp,
  XCircle,
  History,
  Megaphone,
  RefreshCw,
  Volume2,
  VolumeX,
  Lock,
  Smartphone,
  Settings,
  Coins,
  Receipt,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { useProducts } from '../context/ProductContext';
import productService from '../services/productService';
import UserManagement from '../components/admin/UserManagement';
import OrderManagement from '../components/admin/OrderManagement';
import CategoryManagement from '../components/admin/CategoryManagement';
import InventoryManagement from '../components/admin/InventoryManagement';
import DeliverySettings from '../components/admin/DeliverySettings';
import DeliverySlotsSettings from '../components/admin/DeliverySlotsSettings';
import OrderAssignment from '../components/admin/OrderAssignment';
import DeliveryAgents from '../components/admin/DeliveryAgents';
import ServiceCalendar from '../components/admin/ServiceCalendar';
import ReportManagement from '../components/admin/ReportManagement';
import AnalyticsManagement from '../components/admin/AnalyticsManagement';
import ReviewManagement from '../components/admin/ReviewManagement';
import ComplaintManagement from '../components/admin/ComplaintManagement';
import LossManagement from '../components/admin/LossManagement';
import BatchManagement from '../components/admin/BatchManagement';
import FlashDealsManagement from '../components/admin/FlashDealsManagement';
import PromotionalNotifications from '../components/admin/PromotionalNotifications';
import SearchHistoryManagement from '../components/admin/SearchHistoryManagement';
import RefundRequests from '../components/admin/RefundRequests';
import ScheduledListAlerts from '../components/admin/ScheduledListAlerts';
import AccountSecurity from '../components/admin/AccountSecurity';
import AppVersionManagement from '../components/admin/AppVersionManagement';
import GeneralSettings from '../components/admin/GeneralSettings';
import BmfCoinsManagement from '../components/admin/BmfCoinsManagement';
import TaxConfigurationManagement from '../components/admin/TaxConfigurationManagement';
import ManageUnitTypes from '../components/admin/ManageUnitTypes';
import unitTypeService from '../services/unitTypeService';
import { fuzzyMatch } from '../lib/fuzzySearch';
import { ADMIN_NOTIFICATIONS } from '../lib/constants';
import {
  variantUnitsFor,
  conversionFactor,
  composeLabel,
  computeVariantPrice,
  variantCanonicalSize,
  isConvertibleUnit,
} from '../lib/variantUnits';

let sharedAudioCtx = null;
const getAudioCtx = () => {
  if (!sharedAudioCtx) {
    const AudioCtor = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtor) return null;
    sharedAudioCtx = new AudioCtor();
  }
  return sharedAudioCtx;
};

const playOrderAlarm = async () => {
  try {
    const ctx = getAudioCtx();
    if (!ctx) return;
    if (ctx.state === 'suspended') await ctx.resume();
    const now = ctx.currentTime;
    // Two pairs of sharp pulses: beep-beep … beep-beep
    // Square wave gives a harsh buzzer feel; alternating pitch adds urgency.
    const pulses = [
      { offset: 0,    freq: 1046 },
      { offset: 0.14, freq: 880  },
      { offset: 0.38, freq: 1046 },
      { offset: 0.52, freq: 880  },
    ];
    pulses.forEach(({ offset, freq }) => {
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.type = 'square';
      oscillator.frequency.value = freq;
      const startAt = now + offset;
      gain.gain.setValueAtTime(0, startAt);
      gain.gain.linearRampToValueAtTime(0.4, startAt + 0.01);
      gain.gain.setValueAtTime(0.4, startAt + 0.09);
      gain.gain.linearRampToValueAtTime(0, startAt + 0.12);
      oscillator.start(startAt);
      oscillator.stop(startAt + 0.13);
      oscillator.onended = () => {
        oscillator.disconnect();
        gain.disconnect();
      };
    });
  } catch (err) {
    console.warn('[order-alarm] beep failed:', err);
  }
};

// Indian-style abbreviation for large rupee amounts: plain currency up to
// 99,999, then Lac (1,00,000+) and Crore (1,00,00,000+), truncated (not
// rounded) to 2 decimal places, e.g. 723765 -> "₹7.23 Lac".
const formatRevenue = (amount) => {
  const value = Number(amount) || 0;
  const plain = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(value);
  if (value < 100000) return plain;

  const [unitValue, unitLabel] = value < 10000000 ? [value / 100000, 'Lac'] : [value / 10000000, 'Crore'];
  const truncated = Math.floor(unitValue * 100 + 1e-9) / 100;
  return `₹${truncated.toFixed(2)} ${unitLabel}`;
};

const AdminDashboard = () => {
  const { user, isAdmin, isAuthenticated, logout } = useAuth();
  const { products, categories } = useProducts();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Persisted sound preference. Default ON so existing admins don't lose the feature.
  const [soundEnabled, setSoundEnabled] = useState(() => {
    try { return localStorage.getItem('adminSoundEnabled') !== 'false'; } catch { return true; }
  });
  // Ref so the WebSocket closure always reads the latest value without reconnecting.
  const soundEnabledRef = useRef(soundEnabled);
  useEffect(() => { soundEnabledRef.current = soundEnabled; }, [soundEnabled]);
  const [dashboardStats, setDashboardStats] = useState({
    totalProducts: null, revenue: null, activeOrders: null, members: null
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [orderRes, userRes] = await Promise.all([
          api.get('/admin/orders/stats'),
          api.get('/admin/users/stats'),
        ]);
        setDashboardStats({
          totalProducts: products.filter(p => p.isAvailable).length,
          revenue: formatRevenue(orderRes.data.revenue),
          activeOrders: (orderRes.data.pending || 0) + (orderRes.data.processing || 0),
          members: userRes.data.total || 0,
        });
      } catch (e) {
        console.error('Failed to load dashboard stats', e);
      }
    };
    if (isAdmin) fetchStats();
  }, [isAdmin, products.length]);

  const [showAddProduct, setShowAddProduct] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productRefreshTrigger, setProductRefreshTrigger] = useState(0);
  const [pendingCounts, setPendingCounts] = useState({ complaints: 0, refunds: 0, orders: 0, orderAssignment: 0, deliveryAgents: 0 });
  const [seenCounts, setSeenCounts] = useState({ complaints: 0, refunds: 0, orders: 0, orderAssignment: 0, deliveryAgents: 0 });
  const [newItemCounts, setNewItemCounts] = useState({});

  const fetchPendingCounts = useCallback(async () => {
    try {
      const res = await api.get('/admin/pending-counts');
      const counts = {
        complaints: res.data.openComplaints ?? 0,
        refunds: res.data.pendingRefunds ?? 0,
        orders: res.data.pendingOrders ?? 0,
        orderAssignment: res.data.unassignedOrders ?? 0,
        deliveryAgents: res.data.pendingAgentRegistrations ?? 0,
      };
      setPendingCounts(counts);
    } catch { /* silent — badge stays at 0 */ }
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    fetchPendingCounts();
    const id = setInterval(fetchPendingCounts, 30000);
    return () => clearInterval(id);
  }, [isAdmin, fetchPendingCounts]);

  // Unlock AudioContext on any user click anywhere on the page.
  // Browsers suspend AudioContext until a user gesture occurs; checking state on
  // every click also covers Chrome's rare re-suspension after tab focus loss.
  useEffect(() => {
    if (!isAdmin) return;
    const tryUnlock = () => {
      const ctx = getAudioCtx();
      if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {});
    };
    document.addEventListener('click', tryUnlock);
    return () => document.removeEventListener('click', tryUnlock);
  }, [isAdmin]);

  // Toggle order-notification sound on/off.
  // The click handler IS a user gesture, so ctx.resume() is honoured here.
  // Playing a test beep on enable confirms to the admin that audio is unlocked.
  const handleToggleSound = () => {
    const next = !soundEnabledRef.current;
    setSoundEnabled(next);
    soundEnabledRef.current = next;
    try { localStorage.setItem('adminSoundEnabled', String(next)); } catch {}
    const ctx = getAudioCtx();
    if (next && ctx) {
      ctx.resume().then(() => playOrderAlarm()).catch(() => {});
    }
  };

  // OS notifications carry their own system sound, unaffected by autoplay restrictions.
  useEffect(() => {
    if (!isAdmin) return;
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    const wsBaseUrl = (process.env.REACT_APP_API_URL || 'https://bookmyfresh.com/api').replace(/\/api\/?$/, '');
    const socket = io(`${wsBaseUrl}/orders`, { transports: ['websocket'] });
    const onNewOrder = (payload) => {
      if (soundEnabledRef.current) playOrderAlarm();
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        new Notification(ADMIN_NOTIFICATIONS.NEW_ORDER_TITLE, {
          body: payload?.orderNumber
            ? ADMIN_NOTIFICATIONS.NEW_ORDER_BODY_WITH_NUMBER(payload.orderNumber)
            : ADMIN_NOTIFICATIONS.NEW_ORDER_BODY_DEFAULT,
        });
      }
      fetchPendingCounts();
    };
    const onConnectError = (err) => {
      console.error('[order-alarm] socket connect_error:', err.message);
    };
    socket.on('newOrder', onNewOrder);
    socket.on('connect_error', onConnectError);
    return () => {
      socket.off('newOrder', onNewOrder);
      socket.off('connect_error', onConnectError);
      socket.disconnect();
    };
  }, [isAdmin, fetchPendingCounts]);

  // Redirect if not admin
  if (!isAuthenticated || !isAdmin) {
    return <Navigate to="/login" replace />;
  }

  const stats = [
    { label: 'Total Products', value: dashboardStats.totalProducts ?? products.length, icon: Package, color: 'bg-blue-500' },
    { label: 'Total Revenue',  value: dashboardStats.revenue ?? '—',  icon: IndianRupee, color: 'bg-green-500' },
    { label: 'Active Orders',  value: dashboardStats.activeOrders ?? '—', icon: ShoppingCart, color: 'bg-orange-500' },
    { label: 'Members',        value: dashboardStats.members ?? '—', icon: UserCheck, color: 'bg-purple-500' },
  ];

  const badgeCounts = {
    complaints:      Math.max(0, pendingCounts.complaints      - seenCounts.complaints),
    refunds:         Math.max(0, pendingCounts.refunds         - seenCounts.refunds),
    orders:          Math.max(0, pendingCounts.orders          - seenCounts.orders),
    orderAssignment: Math.max(0, pendingCounts.orderAssignment - seenCounts.orderAssignment),
    deliveryAgents:  Math.max(0, pendingCounts.deliveryAgents  - seenCounts.deliveryAgents),
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'products', label: 'Products', icon: Package },
    { id: 'inventory', label: 'Inventory', icon: Warehouse },
    { id: 'batches', label: 'Batches', icon: Clock },
    { id: 'categories', label: 'Categories', icon: FolderTree },
    { id: 'orders', label: 'Orders', icon: ShoppingCart, badge: badgeCounts.orders, countKey: 'orders' },
    { id: 'order-assignment', label: 'Order Assignment', icon: Truck, badge: badgeCounts.orderAssignment, countKey: 'orderAssignment' },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'bmf-coins', label: 'BMF Coins', icon: Coins },
    { id: 'flash-deals', label: 'Flash Deals', icon: Zap },
    { id: 'promotions', label: 'Promotions', icon: Megaphone },
    { id: 'complaints', label: 'Complaints', icon: AlertTriangle, badge: badgeCounts.complaints, countKey: 'complaints' },
    { id: 'reviews', label: 'Reviews', icon: Star },
    { id: 'analytics', label: 'Analytics', icon: TrendingUp },
    { id: 'loss-tracking', label: 'Loss Tracking', icon: Trash2 },
    { id: 'delivery', label: 'Delivery Settings', icon: Truck },
    { id: 'delivery-slots', label: 'Delivery Slots', icon: Clock },
    { id: 'delivery-agents', label: 'Delivery Agents', icon: UserCog, badge: badgeCounts.deliveryAgents, countKey: 'deliveryAgents' },
    { id: 'service-calendar', label: 'Service Calendar', icon: CalendarOff },
    { id: 'reports', label: 'Reports', icon: FileText },
    { id: 'search-history', label: 'Search History', icon: History },
    { id: 'refund-requests', label: 'Refund Requests', icon: RefreshCw, badge: badgeCounts.refunds, countKey: 'refunds' },
    { id: 'account-security', label: 'Login & Password', icon: Lock },
    { id: 'app-version', label: 'App Version', icon: Smartphone },
    { id: 'general-settings', label: 'General Settings', icon: Settings },
    { id: 'tax-configuration', label: 'Tax Configuration', icon: Receipt },
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile Header with Hamburger */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-[#2e4e18] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          <div className="flex items-center gap-2">
            <img src="/logo-symbol.png" alt="Book my fresh" className="w-9 h-9 rounded-lg object-contain" />
            <span className="text-white font-semibold text-sm">Admin</span>
          </div>
        </div>
        <button
          onClick={handleToggleSound}
          title={soundEnabled ? 'Order sound ON — tap to mute' : 'Order sound OFF — tap to enable'}
          className="p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
        >
          {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5 text-red-300" />}
        </button>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className="flex">
        {/* Sidebar */}
        <aside className={`
          w-64 bg-[#2e4e18] h-screen fixed left-0 top-0 z-50 flex flex-col
          transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
        `}>
          {/* Logo Header - Fixed */}
          <div className="p-4 flex-shrink-0">
            <div className="flex items-center gap-3">
              <img src="/logo-symbol.png" alt="Book my fresh" className="w-10 h-10 rounded-lg object-contain flex-shrink-0" />
              <div className="min-w-0">
                <h2 className="text-white font-bold text-sm leading-tight">Book my fresh</h2>
                <p className="text-white/60 text-xs">Admin Dashboard</p>
              </div>
            </div>
          </div>

          {/* Scrollable Nav Content */}
          <div className="flex-1 overflow-y-auto px-4 pb-4 admin-sidebar-scroll">
            <nav className="space-y-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setSidebarOpen(false);
                    if (tab.countKey) {
                      if (tab.badge > 0) {
                        setNewItemCounts(prev => ({ ...prev, [tab.id]: tab.badge }));
                      }
                      setSeenCounts(prev => prev ? { ...prev, [tab.countKey]: pendingCounts[tab.countKey] } : prev);
                    }
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all text-sm ${
                    activeTab === tab.id
                      ? 'bg-[#548B2E] text-white'
                      : 'text-white/70 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <tab.icon className="w-5 h-5 flex-shrink-0" />
                  <span className="flex-1 text-left">{tab.label}</span>
                  {tab.badge > 0 && (
                    <span className="min-w-[20px] h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1 leading-none">
                      {tab.badge > 99 ? '99+' : tab.badge}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          {/* Bottom Section - Fixed */}
          <div className="flex-shrink-0 border-t border-white/10">
            {/* Back to Site Links */}
            <div className="px-4 py-2">
              <Link
                to="/shop"
                onClick={() => setSidebarOpen(false)}
                className="w-full flex items-center gap-3 px-4 py-2 rounded-lg text-white/70 hover:bg-white/10 hover:text-white transition-all text-sm"
              >
                <Home className="w-4 h-4" />
                Back to Site
              </Link>
              <Link
                to="/shop"
                onClick={() => setSidebarOpen(false)}
                className="w-full flex items-center gap-3 px-4 py-2 rounded-lg text-white/70 hover:bg-white/10 hover:text-white transition-all text-sm"
              >
                <ShoppingCart className="w-4 h-4" />
                View Shop
              </Link>
            </div>

            {/* User Info */}
            <div className="p-4 border-t border-white/10">
              <div className="flex items-center gap-3 mb-3">
                <img
                  src={user?.avatar}
                  alt={user?.name}
                  className="w-10 h-10 rounded-full"
                />
                <div>
                  <p className="text-white font-medium text-sm">{user?.name}</p>
                  <p className="text-white/60 text-xs">Administrator</p>
                </div>
              </div>
              
              {/* Sound toggle */}
              <button
                onClick={handleToggleSound}
                title={soundEnabled ? 'Order sound ON — click to mute' : 'Order sound OFF — click to enable'}
                className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-all text-sm mb-1 ${
                  soundEnabled
                    ? 'text-white/70 hover:bg-white/10 hover:text-white'
                    : 'text-red-400/80 hover:bg-red-500/20 hover:text-red-400'
                }`}
              >
                {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                {soundEnabled ? 'Order Sound: ON' : 'Order Sound: OFF'}
              </button>

              {/* Logout Button */}
              <button
                onClick={logout}
                className="w-full flex items-center gap-3 px-4 py-2 rounded-lg text-white/70 hover:bg-red-500/20 hover:text-red-400 transition-all text-sm"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 ml-0 lg:ml-64 p-4 lg:p-8 pt-20 lg:pt-8 min-w-0 overflow-x-hidden">
          {activeTab === 'dashboard' && (
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4">
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Welcome back, {user?.name?.split(' ')[0]}!</h1>
                  <p className="text-gray-600 text-sm sm:text-base">Here's what's happening with your store today.</p>
                </div>
                <button
                  onClick={() => setShowAddProduct(true)}
                  className="flex items-center justify-center gap-2 bg-[#548B2E] hover:bg-[#3f6b20] text-white px-4 py-2 rounded-lg font-medium transition-colors w-full sm:w-auto"
                >
                  <Plus className="w-5 h-5" />
                  Add Product
                </button>
              </div>

              {/* Scheduled slot pick-lists ready to download */}
              <ScheduledListAlerts />

              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {stats.map((stat, index) => (
                  <div key={index} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`${stat.color} w-12 h-12 rounded-lg flex items-center justify-center`}>
                        <stat.icon className="w-6 h-6 text-white" />
                      </div>
                      <span className="text-xs text-gray-400 font-medium">Live</span>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-800">{stat.value}</h3>
                    <p className="text-gray-500 text-sm">{stat.label}</p>
                  </div>
                ))}
              </div>

              {/* Recent Products */}
              <div className="bg-white rounded-xl shadow-sm">
                <div className="p-6 border-b border-gray-100">
                  <h2 className="text-lg font-bold text-gray-800">Recent Products</h2>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    {products.filter(p => p.isAvailable).slice(0, 5).map((product) => (
                      <div key={product.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                        <div className="w-12 h-12 bg-[#f2f8eb] rounded-lg flex items-center justify-center">
                          <Box className="w-6 h-6 text-[#548B2E]" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-800">{product.name}</h4>
                          <p className="text-sm text-gray-500">{product.category}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-gray-800">₹{product.price}</p>
                          <span className={`text-xs px-2 py-1 rounded-full ${product.inStock ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {product.inStock ? 'In Stock' : 'Out of Stock'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'products' && (
            <ProductManagement
              categories={categories}
              setShowAddProduct={setShowAddProduct}
              onEdit={setEditingProduct}
              refreshTrigger={productRefreshTrigger}
            />
          )}

          {activeTab === 'orders' && (
            <OrderManagement newCount={newItemCounts['orders'] || 0} />
          )}

          {activeTab === 'categories' && (
            <CategoryManagement />
          )}

          {activeTab === 'inventory' && (
            <InventoryManagement />
          )}

          {activeTab === 'batches' && (
            <BatchManagement />
          )}

          {activeTab === 'users' && (
            <UserManagement />
          )}

          {activeTab === 'bmf-coins' && (
            <BmfCoinsManagement />
          )}

          {activeTab === 'flash-deals' && (
            <FlashDealsManagement />
          )}

          {activeTab === 'promotions' && (
            <PromotionalNotifications />
          )}

          {activeTab === 'reviews' && (
            <ReviewManagement />
          )}
          {activeTab === 'complaints' && (
            <ComplaintManagement newCount={newItemCounts['complaints'] || 0} />
          )}

          {activeTab === 'analytics' && (
            <AnalyticsManagement />
          )}

          {activeTab === 'loss-tracking' && (
            <LossManagement />
          )}

          {activeTab === 'order-assignment' && (
            <OrderAssignment newCount={newItemCounts['order-assignment'] || 0} />
          )}

          {activeTab === 'delivery' && (
            <DeliverySettings />
          )}

          {activeTab === 'delivery-slots' && (
            <DeliverySlotsSettings />
          )}

          {activeTab === 'service-calendar' && (
            <ServiceCalendar />
          )}

          {activeTab === 'delivery-agents' && (
            <DeliveryAgents newCount={newItemCounts['delivery-agents'] || 0} />
          )}

          {activeTab === 'reports' && (
            <ReportManagement />
          )}

          {activeTab === 'search-history' && (
            <SearchHistoryManagement />
          )}

          {activeTab === 'refund-requests' && (
            <RefundRequests newCount={newItemCounts['refund-requests'] || 0} />
          )}

          {activeTab === 'account-security' && (
            <AccountSecurity />
          )}

          {activeTab === 'app-version' && (
            <AppVersionManagement />
          )}

          {activeTab === 'general-settings' && (
            <GeneralSettings />
          )}

          {activeTab === 'tax-configuration' && (
            <TaxConfigurationManagement />
          )}
        </main>
      </div>

      {/* Add/Edit Product Modal */}
      {(showAddProduct || editingProduct) && (
        <ProductModal
          product={editingProduct}
          onClose={() => {
            setShowAddProduct(false);
            setEditingProduct(null);
          }}
          onSuccess={() => setProductRefreshTrigger(prev => prev + 1)}
        />
      )}
    </div>
  );
};

// Product Management Component
const ProductManagement = ({ categories, setShowAddProduct, onEdit, refreshTrigger }) => {
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [visibilityFilter, setVisibilityFilter] = useState('visible'); // 'visible' | 'hidden'
  const [togglingId, setTogglingId] = useState(null);
  const [showManageUnits, setShowManageUnits] = useState(false); // BMF-87: unit-type manager modal

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await productService.getAllAdmin();
      setAllProducts(data.map(p => ({
        id: p.id,
        name: p.name ?? '',
        description: p.description,
        price: p.pricePerUnit ?? p.price_per_unit,
        weight: p.unitType ?? p.unit_type ?? 'Grams',
        category: p.categoryId ?? p.category_id,
        categoryName: p.category?.name,
        image: p.images?.[0]?.imageUrl ?? p.image ?? '',
        inStock: p.isAvailable !== false && (
          (p.variants?.length > 0)
            ? p.variants.some(v => v.isAvailable && (v.sellable ?? v.stockQuantity) > 0)
            : (p.stockQuantity ?? p.stock_quantity ?? 0) > 0
        ),
        stockQuantity: p.stockQuantity ?? p.stock_quantity ?? 0,
        shelfLifeDays: p.shelfLifeDays ?? p.shelf_life_days,
        isAvailable: p.isAvailable ?? p.is_available ?? true,
        variants: p.variants || [],
        keywords: p.keywords || '',
        hsnCode: p.hsnCode || p.hsn_code || '',
        taxMasterId: p.taxMasterId ?? p.tax_master_id ?? null,
        taxMaster: p.taxMaster ?? null,
      })));
    } catch (err) {
      console.error('Failed to load admin products:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts, refreshTrigger]);

  const handleToggleVisibility = async (product) => {
    setTogglingId(product.id);
    try {
      await productService.toggleVisibility(product.id);
      setAllProducts(prev => prev.map(p =>
        p.id === product.id ? { ...p, isAvailable: !p.isAvailable } : p
      ));
      await refreshProducts();
    } catch (err) {
      console.error('Failed to toggle visibility:', err);
    } finally {
      setTogglingId(null);
    }
  };

  const filteredProducts = allProducts.filter(p => {
    const matchesSearch = fuzzyMatch(searchTerm, p.name);
    const matchesCategory = selectedCategory === 'all' || String(p.category) === String(selectedCategory);
    const matchesVisibility =
      visibilityFilter === 'all' ? true :
      visibilityFilter === 'visible' ? p.isAvailable :
      !p.isAvailable;
    return matchesSearch && matchesCategory && matchesVisibility;
  });

  const hiddenCount = allProducts.filter(p => !p.isAvailable).length;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Products</h1>
          <p className="text-gray-600 text-sm sm:text-base">
            Manage your product catalog
            {hiddenCount > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded-full">
                {hiddenCount} hidden
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchProducts}
            className="p-2 text-gray-500 hover:text-[#548B2E] hover:bg-[#548B2E]/10 rounded-lg transition-colors"
            title="Refresh"
          >
            <Eye className="w-5 h-5" />
          </button>
          {/* BMF-87: manage the Unit Type dropdown options. Same shape as Add
              Product, slate instead of green so the primary action stays distinct. */}
          <button
            onClick={() => setShowManageUnits(true)}
            className="flex items-center gap-2 bg-[#475569] hover:bg-[#3a4557] text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            <Plus className="w-5 h-5" />
            <span className="hidden sm:inline">Add Unit Type</span>
            <span className="sm:hidden">Units</span>
          </button>
          <button
            onClick={() => setShowAddProduct(true)}
            className="flex items-center gap-2 bg-[#548B2E] hover:bg-[#3f6b20] text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            <Plus className="w-5 h-5" />
            <span className="hidden sm:inline">Add Product</span>
            <span className="sm:hidden">Add</span>
          </button>
        </div>
      </div>

      {showManageUnits && (
        <ManageUnitTypes
          onClose={() => setShowManageUnits(false)}
          onChanged={fetchProducts}
        />
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
            />
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 lg:flex-shrink-0">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-400 hidden sm:block" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="flex-1 sm:flex-none px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
              >
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            <select
              value={visibilityFilter}
              onChange={(e) => setVisibilityFilter(e.target.value)}
              className="flex-1 sm:flex-none px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
            >
              <option value="visible">Visible Products</option>
              <option value="hidden">Hidden Products</option>
            </select>
          </div>
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-white rounded-xl shadow-sm">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#548B2E] mx-auto mb-3" />
            <p className="text-gray-500">Loading products...</p>
          </div>
        ) : (
          <div className="overflow-auto max-h-[calc(100vh-240px)]">
            <table className="w-full min-w-[800px]">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-4 lg:px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Product</th>
                  <th className="px-4 lg:px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-4 lg:px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">HSN Code</th>
                  <th className="px-4 lg:px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">GST (%)</th>
                  <th className="px-4 lg:px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Price</th>
                  <th className="px-4 lg:px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Stock</th>
                  <th className="px-4 lg:px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 lg:px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredProducts.map((product) => (
                <tr key={product.id} className={`transition-colors ${product.isAvailable ? 'hover:bg-gray-50' : 'bg-gray-50 opacity-60 hover:opacity-80'}`}>
                  <td className="px-4 lg:px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 lg:w-12 lg:h-12 bg-[#f2f8eb] rounded-lg flex items-center justify-center flex-shrink-0">
                        <Leaf className="w-5 h-5 lg:w-6 lg:h-6 text-[#548B2E]" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-800 truncate">{product.name}</p>
                        <p className="text-sm text-gray-500">{product.weight}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 lg:px-6 py-4">
                    <span className="px-2 lg:px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs lg:text-sm capitalize whitespace-nowrap">
                      {product.categoryName ?? product.category}
                    </span>
                  </td>
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                    {product.hsnCode ? (
                      <span className="text-gray-700 text-xs lg:text-sm font-medium">{product.hsnCode}</span>
                    ) : (
                      <span className="text-gray-400 text-xs lg:text-sm">—</span>
                    )}
                  </td>
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                    {product.taxMaster ? (
                      <span
                        className="px-2 lg:px-3 py-1 bg-[#f2f8eb] text-[#548B2E] rounded-full text-xs lg:text-sm font-medium"
                        title={`${product.taxMaster.description || product.taxMaster.taxCode} — ${Number(product.taxMaster.gstPercentage)}% GST`}
                      >
                        {Number(product.taxMaster.gstPercentage)}%
                      </span>
                    ) : (
                      <span className="text-gray-400 text-xs lg:text-sm">—</span>
                    )}
                  </td>
                  <td className="px-4 lg:px-6 py-4 font-semibold text-gray-800 whitespace-nowrap">₹{product.price}</td>
                  <td className="px-4 lg:px-6 py-4">
                    <span className={`inline-flex items-center gap-1 lg:gap-2 px-2 lg:px-3 py-1 rounded-full text-xs lg:text-sm font-medium ${
                      product.stockQuantity === 0 ? 'bg-red-100 text-red-700'
                      : product.stockQuantity <= 5 ? 'bg-orange-100 text-orange-700'
                      : 'bg-green-100 text-green-700'
                    }`}>
                      <span>{product.stockQuantity}</span>
                      <span className="text-xs opacity-70 hidden lg:inline">
                        {product.stockQuantity === 0 ? '(Out)' : product.stockQuantity <= 5 ? '(Low)' : '(In Stock)'}
                      </span>
                    </span>
                  </td>
                  <td className="px-4 lg:px-6 py-4">
                    {product.isAvailable ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium whitespace-nowrap">
                        <Eye className="w-3 h-3" /> <span className="hidden lg:inline">Visible</span>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-200 text-gray-500 text-xs rounded-full font-medium whitespace-nowrap">
                        <EyeOff className="w-3 h-3" /> <span className="hidden lg:inline">Hidden</span>
                      </span>
                    )}
                  </td>
                  <td className="px-4 lg:px-6 py-4">
                    <div className="flex items-center justify-end gap-1 lg:gap-2">
                      <button
                        onClick={() => onEdit(product)}
                        className="p-1.5 lg:p-2 text-gray-500 hover:text-[#548B2E] hover:bg-[#548B2E]/10 rounded-lg transition-colors"
                        title="Edit product"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleToggleVisibility(product)}
                        disabled={togglingId === product.id}
                        className={`p-1.5 lg:p-2 rounded-lg transition-colors ${
                          product.isAvailable
                            ? 'text-gray-500 hover:text-orange-600 hover:bg-orange-50'
                            : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
                        } disabled:opacity-40 disabled:cursor-not-allowed`}
                        title={product.isAvailable ? 'Hide from shop' : 'Show in shop'}
                      >
                        {product.isAvailable ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}

        {!loading && filteredProducts.length === 0 && (
          <div className="text-center py-12">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No products found</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Product Modal Component
const ProductModal = ({ product, onClose, onSuccess }) => {
  const { addProduct, updateProduct, categories, refreshProducts } = useProducts();
  const isEdit = !!product;

  const existingImageUrl = product?.images?.[0]?.imageUrl || product?.image || null;

  const [formData, setFormData] = useState({
    name: product?.name || '',
    description: product?.description || '',
    price: product?.pricePerUnit || product?.price || '',
    unitType: product?.unitType || product?.weight || '250 Grams',
    categoryId: product?.categoryId || product?.category || '',
    stockQuantity: product?.stockQuantity ?? 0,
    shelfLifeDays: product?.shelfLifeDays ?? 3,
    keywords: product?.keywords || '',
    hsnCode: product?.hsnCode || '',
    taxMasterId: product?.taxMasterId ?? null,
  });

  // BMF-137: Tax Code picker state. taxLabel shows the currently-selected tax on
  // edit; taxResults holds live search matches from tax_master (GOODS only).
  const formatTaxLabel = (t) =>
    t ? `${t.taxCode} | ${t.description || '—'} | ${Number(t.gstPercentage)}% GST` : '';
  const [taxSearch, setTaxSearch] = useState(formatTaxLabel(product?.taxMaster));
  const [taxResults, setTaxResults] = useState([]);
  const [showTaxResults, setShowTaxResults] = useState(false);

  // BMF-87: unit types are admin-managed now — load the active list for the dropdown.
  const [unitTypes, setUnitTypes] = useState([]);
  useEffect(() => {
    let alive = true;
    unitTypeService.getActive()
      .then(list => { if (alive) setUnitTypes(list); })
      .catch(err => console.error('Failed to load unit types:', err));
    return () => { alive = false; };
  }, []);

  // BMF-137: debounced Tax Code search. An empty query intentionally loads the
  // default active-GOODS list so the picker has options the moment it opens. The
  // one call we skip is the redundant one on an edit form: while the input still
  // shows the currently-selected tax's label (nothing typed yet), there is
  // nothing to search for — searching the whole label string would only return
  // "no matches" for an already-valid selection.
  useEffect(() => {
    if (!showTaxResults) return;
    if (formData.taxMasterId && taxSearch === formatTaxLabel(product?.taxMaster)) return;
    let alive = true;
    const t = setTimeout(() => {
      productService.searchTaxMaster(taxSearch.trim())
        .then(rows => { if (alive) setTaxResults(rows); })
        .catch(() => { if (alive) setTaxResults([]); });
    }, 300);
    return () => { alive = false; clearTimeout(t); };
  }, [taxSearch, showTaxResults, formData.taxMasterId, product?.taxMaster]);

  // Keep the product's current unit type selectable even if it has since been
  // deactivated, so editing an old product doesn't silently switch its unit.
  const unitTypeOptions = useMemo(() => {
    const labels = unitTypes.map(u => u.label);
    if (formData.unitType && !labels.includes(formData.unitType)) {
      return [formData.unitType, ...labels];
    }
    return labels;
  }, [unitTypes, formData.unitType]);

  // Keywords tag state
  const [keywordInput, setKeywordInput] = useState('');
  const keywordList = formData.keywords
    ? formData.keywords.split(',').map(k => k.trim()).filter(Boolean)
    : [];

  const addKeyword = (raw) => {
    const tag = raw.trim().toLowerCase();
    if (!tag || keywordList.includes(tag)) return;
    const updated = [...keywordList, tag].join(',');
    setFormData(prev => ({ ...prev, keywords: updated }));
    setKeywordInput('');
  };

  const removeKeyword = (tag) => {
    const updated = keywordList.filter(k => k !== tag).join(',');
    setFormData(prev => ({ ...prev, keywords: updated }));
  };

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(existingImageUrl);
  const [imageError, setImageError] = useState('');
  const [taxError, setTaxError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  // ── Variants state ──
  // poolStock = the product's batch-derived stock (base units). Variant
  // reservations are carved from this; base sellable = pool − Σ(reserved × factor).
  const [poolStock, setPoolStock] = useState(Number(product?.stockQuantity) || 0);
  const [variants, setVariants] = useState(
    (product?.variants || []).map(v => ({ ...v, _saved: true }))
  );
  const baseUnitType = formData.unitType;
  const variantUnitOptions = variantUnitsFor(baseUnitType);
  const [newVariant, setNewVariant] = useState({
    weight: '',
    unit: variantUnitsFor(product?.unitType || product?.weight || '250 Grams')[0],
    pricePerUnit: '',
    stockQuantity: '',
    priceTouched: false,
  });
  const [variantError, setVariantError] = useState('');
  const [savingVariant, setSavingVariant] = useState(false);

  // Keep the add-row's unit valid when the product's dimension changes
  // (e.g. admin switches unit type from "250 Grams" to "Piece").
  useEffect(() => {
    if (!variantUnitOptions.includes(newVariant.unit)) {
      setNewVariant(p => ({ ...p, unit: variantUnitOptions[0], priceTouched: false }));
    }
  }, [variantUnitOptions, newVariant.unit]);

  // Inline edit state for an existing variant row.
  const [editingVariantId, setEditingVariantId] = useState(null);
  const [editDraft, setEditDraft] = useState(null); // { weight, unit, pricePerUnit, stockQuantity, priceTouched }

  // On open (edit), refetch the full product so variants + pool are fresh.
  useEffect(() => {
    if (!isEdit || !product?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const full = await productService.getById(product.id);
        if (cancelled || !full) return;
        setPoolStock(Number(full.stockQuantity) || 0);
        setVariants((full.variants || []).map(v => ({ ...v, _saved: true })));
      } catch { /* keep seeded values */ }
    })();
    return () => { cancelled = true; };
  }, [isEdit, product?.id]);

  // ── Variant math (mirrors backend) ──
  const reservedBaseUnits = (list) => list.reduce((sum, v) => {
    const f = conversionFactor(baseUnitType, Number(v.weight), v.unit);
    return sum + Number(v.stockQuantity || 0) * f;
  }, 0);
  const reservedTotal = reservedBaseUnits(variants);
  const baseAvailable = Math.max(0, poolStock - reservedTotal);
  const overReserved = reservedTotal > poolStock + 1e-9;

  const basePrice = Number(formData.price) || 0;
  // Suggested price for the add-row (null when not derivable).
  const newAutoPrice = newVariant.weight
    ? computeVariantPrice(basePrice, baseUnitType, Number(newVariant.weight), newVariant.unit)
    : null;
  const newPriceShown = newVariant.priceTouched
    ? newVariant.pricePerUnit
    : (newAutoPrice != null ? String(newAutoPrice) : '');
  const newFactor = newVariant.weight
    ? conversionFactor(baseUnitType, Number(newVariant.weight), newVariant.unit)
    : 0;
  const newNeeded = (Number(newVariant.stockQuantity) || 0) * newFactor;

  const resetNewVariant = () => setNewVariant({
    weight: '', unit: variantUnitOptions[0], pricePerUnit: '', stockQuantity: '', priceTouched: false,
  });

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowed.includes(file.type)) {
      setImageError('Only PNG, JPG, or WebP images are allowed.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setImageError('Image must be under 5 MB.');
      return;
    }

    setImageError('');
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    e.target.value = '';
  };

  const clearImage = () => {
    setImageFile(null);
    setImagePreview(isEdit ? existingImageUrl : null);
    setImageError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isEdit && !imageFile) {
      setImageError('A product image is required.');
      return;
    }

    // Tax Code is mandatory — a product must reference a GST tax definition.
    if (!formData.taxMasterId) {
      setTaxError('Please select a Tax Code.');
      return;
    }
    setTaxError('');

    setIsSubmitting(true);
    let createdProductId = null;
    try {
      if (isEdit) {
        await updateProduct(product.id, formData);
        if (imageFile) {
          await productService.uploadImage(product.id, imageFile);
          await refreshProducts();
        }
      } else {
        const newProduct = await addProduct(formData);
        createdProductId = newProduct?.id;
        await productService.uploadImage(createdProductId, imageFile);
      }
      onSuccess();
      onClose();
    } catch (error) {
      // If image upload failed after product was created, clean it up
      if (!isEdit && createdProductId && error._isImageUpload) {
        try { await productService.delete(createdProductId); } catch { /* ignore */ }
      }
      alert('Failed to save product: ' + (error.response?.data?.message || error.message || 'Unknown error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-800">
            {isEdit ? 'Edit Product' : 'Add New Product'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">

          {/* ── Image upload ── */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Product Image <span className="text-red-500">*</span>
              <span className="text-gray-400 font-normal ml-1">(PNG, JPG, WebP · max 5 MB)</span>
            </label>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              onChange={handleFileChange}
              className="hidden"
            />

            {imagePreview ? (
              <div className="relative w-full h-44 rounded-xl overflow-hidden border border-gray-200 bg-gray-50 group">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="px-3 py-1.5 bg-white text-gray-800 text-sm font-medium rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    Replace
                  </button>
                  {(!isEdit || imageFile) && (
                    <button
                      type="button"
                      onClick={clearImage}
                      className="p-1.5 bg-white text-red-500 rounded-lg hover:bg-red-50 transition-colors"
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full h-36 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center gap-2 text-gray-400 hover:border-[#548B2E] hover:text-[#548B2E] transition-colors"
              >
                <ImageUp className="w-8 h-8" />
                <span className="text-sm font-medium">Click to upload image</span>
                <span className="text-xs">PNG, JPG, WebP</span>
              </button>
            )}

            {imageError && (
              <p className="text-red-500 text-xs mt-1">{imageError}</p>
            )}
          </div>

          {/* ── Product Name ── */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
              required
            />
          </div>

          {/* ── Description ── */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
              required
            />
          </div>

          {/* ── Price + Unit ── */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Price (₹)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: Number(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Unit Type</label>
              <select
                value={formData.unitType}
                onChange={(e) => setFormData({ ...formData, unitType: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
              >
                {unitTypeOptions.map(label => (
                  <option key={label} value={label}>{label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* ── Shelf Life ── (Stock quantity is managed via Inventory, not here) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Shelf Life (days)</label>
            <input
              type="number"
              min="1"
              value={formData.shelfLifeDays}
              onChange={(e) => setFormData({ ...formData, shelfLifeDays: Number(e.target.value) })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
              required
            />
            <p className="text-xs text-gray-500 mt-1">Days usable after harvest</p>
          </div>

          {/* ── Category ── */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={formData.categoryId}
              onChange={(e) => setFormData({ ...formData, categoryId: Number(e.target.value) })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
              required
            >
              <option value="">Select a category</option>
              {categories.filter(c => c.id !== 'all').map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          {/* ── HSN Code ── */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              HSN Code <span className="text-gray-400 font-normal">(for GST invoice)</span>
            </label>
            <input
              type="text"
              value={formData.hsnCode}
              onChange={(e) => setFormData({ ...formData, hsnCode: e.target.value })}
              placeholder="e.g. 0701 (vegetables), 0803 (bananas), 0804 (mangoes)"
              maxLength={10}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            />
            <p className="text-xs text-gray-400 mt-1">Leave blank if not applicable. Fresh vegetables: 07xx · Fresh fruits: 08xx</p>
          </div>

          {/* ── Tax Code (BMF-137) ── */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tax Code <span className="text-red-500">*</span> <span className="text-gray-400 font-normal">(GST tax definition)</span>
            </label>
            <input
              type="text"
              value={taxSearch}
              onChange={(e) => {
                setTaxSearch(e.target.value);
                setShowTaxResults(true);
                // Typing invalidates the current selection until one is picked.
                if (formData.taxMasterId) setFormData({ ...formData, taxMasterId: null });
              }}
              onFocus={() => setShowTaxResults(true)}
              onBlur={() => setTimeout(() => setShowTaxResults(false), 150)}
              placeholder="Search by tax code, description or GST % (e.g. 0703, 18)"
              className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 ${taxError ? 'border-red-400' : 'border-gray-300'}`}
            />
            {formData.taxMasterId && (
              <button
                type="button"
                onClick={() => { setFormData({ ...formData, taxMasterId: null }); setTaxSearch(''); }}
                className="absolute right-2 top-8 text-xs text-gray-400 hover:text-red-500"
              >
                Clear
              </button>
            )}
            {showTaxResults && taxResults.length > 0 && (
              <ul className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg">
                {taxResults.map((t) => (
                  <li key={t.id}>
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setFormData({ ...formData, taxMasterId: t.id });
                        setTaxSearch(formatTaxLabel(t));
                        setShowTaxResults(false);
                        setTaxError('');
                      }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-green-50"
                    >
                      <span className="font-medium text-gray-800">{t.taxCode}</span>
                      <span className="text-gray-500"> | {t.description || '—'} | </span>
                      <span className="text-green-700 font-medium">{Number(t.gstPercentage)}% GST</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {showTaxResults && taxSearch.trim() && taxResults.length === 0 && (
              <p className="text-xs text-gray-400 mt-1">No matching active tax codes found.</p>
            )}
            {taxError && (
              <p className="text-red-500 text-xs mt-1">{taxError}</p>
            )}
            <p className="text-xs text-gray-400 mt-1">Only active Goods (HSN) tax definitions can be assigned. Tax-exempt products use a 0% GST code.</p>
          </div>

          {/* ── Keywords ── */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search Keywords <span className="text-gray-400 font-normal">(Hinglish / alternate names)</span>
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={keywordInput}
                onChange={e => setKeywordInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    addKeyword(keywordInput);
                  }
                }}
                placeholder="Type a keyword and press Enter (e.g. tamatar, lal sabzi)"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => addKeyword(keywordInput)}
                className="px-3 py-2 bg-[#548B2E] text-white rounded-lg text-sm hover:bg-[#3f6b20]"
              >
                Add
              </button>
            </div>
            {keywordList.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {keywordList.map(tag => (
                  <span key={tag} className="flex items-center gap-1 px-2.5 py-1 bg-[#f0f7e8] text-[#2e4e18] text-xs rounded-full border border-[#548B2E]/30">
                    {tag}
                    <button type="button" onClick={() => removeKeyword(tag)} className="text-[#548B2E] hover:text-red-500 leading-none">×</button>
                  </span>
                ))}
              </div>
            )}
            <p className="text-xs text-gray-400 mt-1">Press Enter or comma after each keyword. Users searching any of these will find this product.</p>
          </div>

          {/* Custom (admin-added) unit types have no size basis, so variants
              can't be priced against them (BMF-87). */}
          {isEdit && !isConvertibleUnit(formData.unitType) && (
            <div className="border border-gray-200 rounded-lg p-4 text-xs text-gray-500 bg-gray-50">
              Variants aren't available for the "{formData.unitType}" unit type — it's a custom unit type without a size basis. Use a built-in unit type (e.g. weight or Piece) to add pack-size variants.
            </div>
          )}

          {/* ── Variants (only on edit — product must exist first) ── */}
          {isEdit && isConvertibleUnit(formData.unitType) && (
            <div className="border border-gray-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-1">
                <p className="text-sm font-semibold text-gray-700">
                  Variants <span className="text-gray-400 font-normal">(alternate pack sizes)</span>
                </p>
                <p className="text-xs text-gray-500">
                  Pool: <span className="font-medium">{poolStock}</span> ·
                  Reserved: <span className="font-medium">{Math.round(reservedTotal * 100) / 100}</span> ·
                  Base available: <span className={`font-medium ${baseAvailable <= 0 ? 'text-red-600' : 'text-green-700'}`}>{Math.round(baseAvailable * 100) / 100}</span>
                </p>
              </div>

              {overReserved && (
                <div className="flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-amber-800">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>Variants reserve more than the available pool ({Math.round(reservedTotal * 100) / 100} of {poolStock}). Restock or reduce a reservation — variant sales are capped by the physical pool.</span>
                </div>
              )}

              {/* Existing variants list */}
              {variants.length > 0 && (
                <div className="space-y-2">
                  {variants.map((v) => {
                    const isEditing = editingVariantId === v.id;
                    if (isEditing && editDraft) {
                      const f = editDraft.weight ? conversionFactor(baseUnitType, Number(editDraft.weight), editDraft.unit) : 0;
                      const auto = editDraft.weight ? computeVariantPrice(basePrice, baseUnitType, Number(editDraft.weight), editDraft.unit) : null;
                      const priceShown = editDraft.priceTouched ? editDraft.pricePerUnit : (auto != null ? String(auto) : '');
                      return (
                        <div key={v.id} className="bg-gray-50 rounded-lg px-3 py-2 space-y-2">
                          <div className="flex gap-2 items-end">
                            <div className="w-16">
                              <label className="block text-[10px] text-gray-500 mb-0.5">Weight</label>
                              <input type="number" min="0.01" step="0.01" value={editDraft.weight}
                                onChange={e => setEditDraft(d => ({ ...d, weight: e.target.value }))}
                                className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm" />
                            </div>
                            <div className="w-20">
                              <label className="block text-[10px] text-gray-500 mb-0.5">Unit</label>
                              <select value={editDraft.unit}
                                onChange={e => setEditDraft(d => ({ ...d, unit: e.target.value }))}
                                className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm">
                                {variantUnitOptions.map(u => <option key={u} value={u}>{u}</option>)}
                              </select>
                            </div>
                            <div className="w-20">
                              <label className="block text-[10px] text-gray-500 mb-0.5">Price ₹{editDraft.priceTouched ? '' : ' (auto)'}</label>
                              <input type="number" min="0" step="0.01" value={priceShown}
                                onChange={e => setEditDraft(d => ({ ...d, pricePerUnit: e.target.value, priceTouched: true }))}
                                className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm" />
                            </div>
                            <div className="w-16">
                              <label className="block text-[10px] text-gray-500 mb-0.5">Stock</label>
                              <input type="number" min="0" value={editDraft.stockQuantity}
                                onChange={e => setEditDraft(d => ({ ...d, stockQuantity: e.target.value }))}
                                className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm" />
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] text-gray-500">Uses {Math.round((Number(editDraft.stockQuantity) || 0) * f * 100) / 100} of pool</span>
                            <button type="button" disabled={savingVariant}
                              onClick={async () => {
                                setVariantError('');
                                // Reject a size that collides with another variant,
                                // even across units (500 g vs 0.5 Kg). Mirrors backend.
                                const editCanonical = variantCanonicalSize(Number(editDraft.weight), editDraft.unit);
                                const dupVariant = variants.find(
                                  x => x.id !== v.id &&
                                    Math.abs(variantCanonicalSize(Number(x.weight), x.unit) - editCanonical) < 1e-6
                                );
                                if (dupVariant) {
                                  setVariantError(`"${dupVariant.label}" already covers this size — ${composeLabel(Number(editDraft.weight), editDraft.unit)} is the same amount. Use a different size.`);
                                  return;
                                }
                                setSavingVariant(true);
                                try {
                                  const updated = await productService.updateVariant(product.id, v.id, {
                                    weight: editDraft.weight,
                                    unit: editDraft.unit,
                                    stockQuantity: parseInt(editDraft.stockQuantity) || 0,
                                    ...(editDraft.priceTouched ? { pricePerUnit: editDraft.pricePerUnit } : {}),
                                  });
                                  setVariants(prev => prev.map(x => x.id === v.id ? { ...updated, _saved: true } : x));
                                  setEditingVariantId(null); setEditDraft(null);
                                  await refreshProducts();
                                } catch (err) {
                                  setVariantError(err.response?.data?.message || 'Failed to update variant');
                                } finally { setSavingVariant(false); }
                              }}
                              className="ml-auto px-3 py-1.5 bg-[#548B2E] text-white rounded-md text-xs hover:bg-[#3f6b20] disabled:opacity-50">
                              Save
                            </button>
                            <button type="button" onClick={() => { setEditingVariantId(null); setEditDraft(null); }}
                              className="px-3 py-1.5 border border-gray-300 text-gray-600 rounded-md text-xs hover:bg-gray-100">
                              Cancel
                            </button>
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div key={v.id} className="flex items-center gap-2 text-sm bg-gray-50 rounded-lg px-3 py-2">
                        <span className="font-medium text-gray-800 w-20 shrink-0">{v.label}</span>
                        <span className="text-gray-600">₹{Number(v.pricePerUnit).toFixed(2)}</span>
                        <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${v.stockQuantity > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {v.stockQuantity > 0 ? `${v.stockQuantity} reserved` : 'None reserved'}
                        </span>
                        <button type="button" title="Edit variant"
                          onClick={() => {
                            setEditingVariantId(v.id);
                            setEditDraft({
                              weight: String(v.weight ?? ''),
                              unit: v.unit || variantUnitOptions[0],
                              pricePerUnit: String(v.pricePerUnit ?? ''),
                              stockQuantity: String(v.stockQuantity ?? ''),
                              priceTouched: true,
                            });
                          }}
                          className="text-gray-400 hover:text-[#548B2E] ml-1">
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                        <button type="button" title="Delete variant"
                          onClick={async () => {
                            if (!window.confirm(`Delete variant "${v.label}"?`)) return;
                            await productService.deleteVariant(product.id, v.id);
                            setVariants(prev => prev.filter(x => x.id !== v.id));
                            await refreshProducts();
                          }}
                          className="text-red-400 hover:text-red-600">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Add new variant row */}
              <div className="flex gap-2 items-end">
                <div className="w-16">
                  <label className="block text-xs text-gray-500 mb-1">Weight</label>
                  <input
                    type="number" min="0.01" step="0.01" placeholder="1"
                    value={newVariant.weight}
                    onChange={e => setNewVariant(p => ({ ...p, weight: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
                  />
                </div>
                <div className="w-20">
                  <label className="block text-xs text-gray-500 mb-1">Unit</label>
                  <select
                    value={newVariant.unit}
                    onChange={e => setNewVariant(p => ({ ...p, unit: e.target.value }))}
                    className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
                  >
                    {variantUnitOptions.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <div className="w-24">
                  <label className="block text-xs text-gray-500 mb-1">
                    Price ₹{newVariant.priceTouched ? '' : ' (auto)'}
                  </label>
                  <input
                    type="number" min="0" step="0.01"
                    placeholder="0.00"
                    value={newPriceShown}
                    onChange={e => setNewVariant(p => ({ ...p, pricePerUnit: e.target.value, priceTouched: true }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
                  />
                </div>
                <div className="w-16">
                  <label className="block text-xs text-gray-500 mb-1">Stock</label>
                  <input
                    type="number" min="0"
                    placeholder="0"
                    value={newVariant.stockQuantity}
                    onChange={e => setNewVariant(p => ({ ...p, stockQuantity: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
                  />
                </div>
                <button
                  type="button"
                  disabled={savingVariant}
                  onClick={async () => {
                    if (!newVariant.weight || Number(newVariant.weight) <= 0) { setVariantError('Enter a weight greater than 0'); return; }
                    // Reject a size that already exists, even in a different unit
                    // (e.g. 500 g vs 0.5 Kg). Mirrors the backend guard.
                    const newCanonical = variantCanonicalSize(Number(newVariant.weight), newVariant.unit);
                    const dupVariant = variants.find(
                      v => Math.abs(variantCanonicalSize(Number(v.weight), v.unit) - newCanonical) < 1e-6
                    );
                    if (dupVariant) {
                      setVariantError(`"${dupVariant.label}" already covers this size — ${composeLabel(Number(newVariant.weight), newVariant.unit)} is the same amount. Use a different size.`);
                      return;
                    }
                    if (newNeeded > baseAvailable + 1e-9) {
                      setVariantError(`Reserving ${Math.round(newNeeded * 100) / 100} base units exceeds the ${Math.round(baseAvailable * 100) / 100} available. Reduce stock or restock the product.`);
                      return;
                    }
                    setVariantError('');
                    setSavingVariant(true);
                    try {
                      const created = await productService.createVariant(product.id, {
                        weight: newVariant.weight,
                        unit: newVariant.unit,
                        stockQuantity: parseInt(newVariant.stockQuantity) || 0,
                        ...(newVariant.priceTouched ? { pricePerUnit: newVariant.pricePerUnit } : {}),
                      });
                      setVariants(prev => [...prev, { ...created, _saved: true }]);
                      resetNewVariant();
                      await refreshProducts();
                    } catch (err) {
                      setVariantError(err.response?.data?.message || 'Failed to add variant');
                    } finally {
                      setSavingVariant(false);
                    }
                  }}
                  className="px-3 py-2 bg-[#548B2E] text-white rounded-lg text-sm hover:bg-[#3f6b20] disabled:opacity-50 shrink-0"
                >
                  {savingVariant ? '…' : '+ Add'}
                </button>
              </div>
              {newVariant.weight && newFactor > 0 && (
                <p className="text-[11px] text-gray-500">
                  {composeLabel(Number(newVariant.weight), newVariant.unit)} ·
                  uses {Math.round(newNeeded * 100) / 100} of pool per {newVariant.stockQuantity || 0} reserved
                  {newAutoPrice != null && !newVariant.priceTouched ? ` · suggested ₹${newAutoPrice}` : ''}
                </p>
              )}
              {variantError && <p className="text-xs text-red-500">{variantError}</p>}
            </div>
          )}

          {/* ── Actions ── */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || (!isEdit && !imageFile)}
              className="flex-1 px-4 py-2 bg-[#548B2E] text-white rounded-lg hover:bg-[#3f6b20] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Saving…' : (isEdit ? 'Update Product' : 'Add Product')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminDashboard;